import React, { useContext, useEffect, useState } from 'react';
import { CapacityData } from '../../templates/StructuredDataContext';
import {
    ProductPagePreview,
    useProducts,
} from '../Global/DataUtils/productQueries';
import { extractSpecificationsImage } from '../ProductSupportLayout/productSupportUtils';
import { PageMetadataContext } from '../../templates/PageMetaDataContext';
import { normalizeLocale } from '../../utils/locale';
import { pickLocalizedEdges } from '../../utils/localizedNodes';
import WhatSaunaResults from './WhatSaunaResults';
import { graphql, useStaticQuery } from 'gatsby';
import WhatSaunaForm from './WhatSaunaForm';

interface Props {
    kontentItemId?: string;
    results_headline: { value: string };
    selectedWellnessTags: string[];
    selectedCapacity: string;
    allProducts: ProductPagePreview[];
}

interface Capacity {
    name?: string;
    codename?: string;
    value?: number;
}
interface WellnessType {
    codename?: string;
    name?: string;
    img?: string;
}

interface WellnessQueryResults {
    allKontentItemMediaWellnessImage: {
        edges: {
            node: {
                preferred_language?: string;
                elements: {
                    image: {
                        value: {
                            url: string;
                            description?: string;
                            name?: string;
                            width?: number;
                            height?: number;
                        }[];
                    };
                    wellness_type: {
                        value: {
                            codename: string;
                        }[];
                    };
                };
            };
        }[];
    };
}

export interface ParsedSaunaProducts {
    name?: string;
    description?: string;
    wellnessTags?: {
        codename: string;
        name: string;
    }[];
    capacity?: {
        name?: string;
        codename?: string;
        value?: number;
    };
    image: { url: string; description: string; name: string };
    slug: string;
}

function parseProducts(
    allProducts: ProductPagePreview[],
    currentLanguage: string | undefined
): ParsedSaunaProducts[] {
    const products = [];

    for (let i = 0; i < allProducts.length; i++) {
        let product = allProducts[i];

        const wellnessTags =
            currentLanguage == 'de-de'
                ? product?.elements?.wellness_type___de_de?.value
                : currentLanguage == 'fr-fr'
                ? product?.elements?.wellness_type___fr_fr?.value
                : product?.elements?.wellness_type?.value;

        if (wellnessTags.length) {
            const description =
                product?.elements?.page_components?.value?.filter(
                    (p) =>
                        p.__typename ===
                        'kontent_item_component___product_description'
                );
            const specs = product?.elements?.page_components?.value?.filter(
                (p) =>
                    p.__typename ===
                    'kontent_item_component___product_specifications'
            );
            const capacity = specs[0]?.elements?.specs?.value[0]?.elements
                ?.capacity?.value[0] as CapacityData;
            const image = extractSpecificationsImage(
                product?.elements?.page_components as any
            ) || { url: '', name: '', description: '' };

            products.push({
                name: product?.elements?.display_page_name?.value,
                description: description[0]?.elements?.short_description?.value,
                wellnessTags: wellnessTags,
                capacity: {
                    name: capacity?.elements?.[
                        currentLanguage == 'de-de'
                            ? `capacity_tag___de_de`
                            : `capacity_tag`
                    ]?.value[0]?.name,
                    codename:
                        capacity?.elements?.capacity_tag?.value[0]?.codename,
                    value: capacity?.elements?.capacity_value?.value,
                },
                image: image,
                slug:
                    product?.elements?.slug?.url ||
                    product?.elements?.slug?.value ||
                    '',
            });
        }
    }

    return products;
}

const SaunaQuizResults: React.FC<Props> = ({ ...props }) => {
    const [showForm, setshowForm] = useState(null);
    const [matchedProducts, setmatchedProducts] = useState([]);
    let { wellness_type, results_headline, ...rest } = props;
    const pageMetadata = useContext(PageMetadataContext);

    const allProducts = useProducts().filter(
        (product) =>
            normalizeLocale(product.preferred_language) ==
            normalizeLocale(pageMetadata.preferredLanguage)
    );
    const currentLanguage = useContext(PageMetadataContext)?.preferredLanguage;

    useEffect(() => {
        const windowLocationSearch =
            typeof window === 'undefined' ? '' : window.location.search;

        const queryParams = new URLSearchParams(windowLocationSearch);

        const paramsObject = Object.fromEntries(queryParams.entries());
        const selectedWellnessTags = paramsObject?.wellness?.split(',');
        const selectedCapacity = paramsObject?.capacity;
        setshowForm(
            selectedCapacity == undefined ||
                selectedWellnessTags == undefined ||
                selectedWellnessTags?.length == 0
        );
        const parsedProducts = parseProducts(allProducts, currentLanguage);
        const matchingProds = parsedProducts.filter((product) => {
            if (
                product?.capacity?.codename === selectedCapacity &&
                product?.wellnessTags?.length
            ) {
                for (let i = 0; i < product?.wellnessTags?.length; i++) {
                    if (
                        selectedWellnessTags?.includes(
                            product.wellnessTags[i].codename
                        )
                    ) {
                        return true;
                    }
                }
            }
            return false;
        });
        setmatchedProducts(matchingProds);
    }, []);
    const wellnessImagesQueryResults = useStaticQuery(graphql`
        query WellnessImageQuery {
            allKontentItemMediaWellnessImage {
                edges {
                    node {
                        preferred_language
                        elements {
                            image {
                                value {
                                    url
                                    description
                                    name
                                    width
                                    height
                                }
                            }
                            wellness_type {
                                value {
                                    codename
                                }
                            }
                            wellness_type___de_de {
                                value {
                                    codename
                                }
                            }
                            wellness_type___fr_fr {
                                value {
                                    codename
                                }
                            }
                        }
                    }
                }
            }
        }
    `);

    wellness_type = addWellnessImages(
        wellness_type?.value,
        wellnessImagesQueryResults,
        pageMetadata?.preferredLanguage
    );

    return (
        <div data-kontent-item-id={props.kontentItemId}>
            {showForm === null ? null : showForm ? (
                <WhatSaunaForm
                    {...props}
                    wellness_type={wellness_type}
                    allProducts={allProducts}
                    OpenInSameComponent={true}
                />
            ) : (
                <WhatSaunaResults
                    results_headline={results_headline?.value}
                    results={matchedProducts}
                    {...rest}
                />
            )}
        </div>
    );
};

function addWellnessImages(
    wellnessTypeValues: WellnessType[],
    wellnessImageQueryResults: WellnessQueryResults,
    preferredLanguage?: string
) {
    const wellnessImages = pickLocalizedEdges(
        wellnessImageQueryResults?.allKontentItemMediaWellnessImage?.edges,
        preferredLanguage
    ).map((result) => {
        return {
            img: (() => {
                const asset = result?.node?.elements?.image?.value[0];
                return asset?.url
                    ? {
                          url: asset.url,
                          width: asset.width || 152,
                          height: asset.height || 152,
                      }
                    : '';
            })(),
            codename: result?.node?.elements?.wellness_type?.value[0]?.codename,
        };
    });

    const wellnessTypeWithImages = wellnessTypeValues?.map((wt) => {
        const wellnessTypeImage = wellnessImages.find((wellnessImage) => {
            return wt.codename === wellnessImage.codename;
        });
        wt.img = wellnessTypeImage?.img || '';

        return wt;
    });

    return { value: wellnessTypeWithImages };
}
export default SaunaQuizResults;

export const fragmentSaunaQuizResults = graphql`
    fragment SaunaQuizResults on kontent_item_component___sauna_quiz_results {
        elements {
            headline {
                value
            }
            description_copy {
                ...kontentRichText
            }
            interested_in_subheadline {
                value
            }
            cta_label {
                value
            }
            anchor_name {
                value
            }
            contact_form {
                value
            }
            capacity_need_subheadline {
                value
            }
            capacity_tag {
                value {
                    codename
                    name
                }
            }
            capacity_tag_for_german_site {
                value {
                    codename
                    name
                }
            }
            capacity_tag_for_french_site {
                value {
                    codename
                    name
                }
            }
            wellness_type {
                value {
                    codename
                    name
                }
            }
            wellness_type_for_german_site {
                value {
                    codename
                    name
                }
            }
            wellness_type_for_french_site {
                value {
                    codename
                    name
                }
            }
            background_image {
                ...kontentImage
            }
            results_headline {
                value
            }
            cta_configuration__cta_label {
                value
            }
            cta_configuration__cta_location {
                ...kontentCtaLocation
            }
            cta_configuration__cta_location_anchor_point {
                value
            }
            cta_configuration__cta_window_action {
                value {
                    codename
                }
            }
            show_2nd_cta {
                value {
                    codename
                }
            }
        }
    }
`;
